import { QuestDefinition } from './types';
import { lineDetectionQuest } from './lineDetectionQuest';
import { autoFloorQuest } from './autoFloorQuest';
import { cameraOrientationQuest } from './cameraOrientationQuest';
import { manualFloorQuest } from './manualFloorQuest';
import { manualRoomDrawQuest } from './manualRoomDrawQuest';
import { wallSketchEditorQuest } from './wallSketchEditorQuest';
import { outlineCreateQuest } from './outlineCreateQuest';
import { roomMergeQuest } from './roomMergeQuest';
import { wallCreateQuest } from './wallCreateQuest';
import { wallEditQuest } from './wallEditQuest';

/** Public interface for QuestRegistry consumers */
export interface IQuestRegistry {
  register(quest: QuestDefinition): void;
  resolveTooltipKey(toolName: string, phase: string | undefined): string | undefined;
  resolveOverrideKey(toolName: string, overrideId: string): string | undefined;
  getOverrideKeys(toolName: string): Record<string, string>;
  hasQuest(toolName: string): boolean;
}

/**
 * QuestRegistry - Singleton that stores all quest definitions.
 *
 * Used by the React client (via viewer.personaManager.questRegistry) to resolve
 * tooltip translation keys from tool state. Tools dispatch phases, this registry
 * maps them to translation source strings.
 */
class QuestRegistryClass implements IQuestRegistry {
  private quests: Map<string, QuestDefinition> = new Map();

  constructor() {
    this.register(lineDetectionQuest);
    this.register(autoFloorQuest);
    this.register(cameraOrientationQuest);
    this.register(manualFloorQuest);
    this.register(manualRoomDrawQuest);
    this.register(wallSketchEditorQuest);
    this.register(outlineCreateQuest);
    this.register(roomMergeQuest);
    this.register(wallCreateQuest);
    this.register(wallEditQuest);
  }

  /** Register a quest definition for a tool */
  register(quest: QuestDefinition): void {
    this.quests.set(quest.toolName, quest);
  }

  /**
   * Resolve a tooltip translation source string for a tool given its current phase.
   *
   * @param toolName - The tool's name (e.g., 'line_detection')
   * @param phase - The current phase from the tool's state payload
   * @returns Translation source string, or undefined if no quest/phase match
   */
  resolveTooltipKey(toolName: string, phase: string | undefined): string | undefined {
    const quest = this.quests.get(toolName);
    if (!quest) return undefined;

    if (phase && quest.states[phase]) {
      return quest.states[phase];
    }

    return quest.defaultKey;
  }

  /**
   * Resolve an override translation source string by tool name and override ID.
   *
   * @param toolName - The tool's name (e.g., 'line_detection')
   * @param overrideId - The override identifier (e.g., 'shift_snap')
   * @returns Translation source string, or undefined if no match
   */
  resolveOverrideKey(toolName: string, overrideId: string): string | undefined {
    const quest = this.quests.get(toolName);
    return quest?.overrides?.[overrideId];
  }

  /**
   * Get all override keys for a tool (used by React hook to pre-translate).
   *
   * @param toolName - The tool's name
   * @returns Record of overrideId → translation source string (empty if none)
   */
  getOverrideKeys(toolName: string): Record<string, string> {
    const quest = this.quests.get(toolName);
    return quest?.overrides ?? {};
  }

  /** Check if a tool has a quest definition */
  hasQuest(toolName: string): boolean {
    return this.quests.has(toolName);
  }
}

export const QuestRegistry = new QuestRegistryClass();
